import React, { createContext, useContext, useState, useEffect } from 'react';
import { useError } from './ErrorContext';

const EmailSignatureContext = createContext();

// Initial values structure for each email signature
const initialEmailSignatureValues = {
  emailSignatureProfilePictureUrl: '',
  emailSignatureCompanyLogoUrl: '',
  emailSignatureQRCodeLogoUrl: '',
  emailSignatureBannerImageUrl: '',
  emailSignatureName: '',
  emailSignatureJobTitle: '',
  emailSignatureCompany: '',
  emailSignaturePhoneNumber: '',
  emailSignatureLocation: '',
  emailSignatureDisclaimer: '',
  emailSignatureQRCodeData: '', // Add this to store the full QR code
  emailSignatureLinkTypes: [], // Array of selected link types for email signature

  emailSignatureIncludeBannerImage: false, // Include banner image by default
  emailSignatureIncludeQRCode: true, // Include QR code by default
  
  // Visibility flags for each field
  emailSignatureShowName: true,
  emailSignatureShowPronouns: false,
  emailSignatureShowJobTitle: true,
  emailSignatureShowCompany: true,
  emailSignatureShowLocation: true,
  emailSignatureShowPhoneNumber: true,
  emailSignatureShowProfilePic: true,
  emailSignatureShowCompanyLogo: true,
};

export const EmailSignatureProvider = ({ children }) => {
  const [emailSignatureState, setEmailSignatureState] = useState({});
  const [error, setError] = useState(null);

  // Get error context for global error handling
  const { addError } = useError();

  // Enhanced error handling for email signature operations
  const handleEmailSignatureError = (error, operation = 'email-signature-operation') => {
    console.error('Email signature operation error:', error);

    // Only show user-relevant errors, log technical errors
    if (error.message?.includes('validation') ||
      error.message?.includes('must be') ||
      error.message?.includes('required') ||
      error.message?.includes('permission') ||
      error.message?.includes('session')) {
      addError(error, {
        source: 'email-signature',
        type: operation,
        retryable: false,
        autoDismiss: true
      });
    }

    setError(error.message || 'An error occurred while updating email signature');
  };

  // Clear error after timeout
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Function to update the email signature state for a specific card with error handling
  const updateEmailSignatureData = (cardId, newData) => {
    try {
      if (!cardId) {
        throw new Error('Card ID is required to update email signature data');
      }

      setEmailSignatureState(prevState => {
        const updatedState = {
          ...prevState,
          [cardId]: {
            ...prevState[cardId],
            ...newData,
          },
        };

        console.log('Email signature values updated from context file:', updatedState[cardId]);
        console.log('context Name:', updatedState[cardId].emailSignatureName);
        console.log('context Job Title:', updatedState[cardId].emailSignatureJobTitle);
        console.log('context Company:', updatedState[cardId].emailSignatureCompany);
        console.log('context Location:', updatedState[cardId].emailSignatureLocation);

        return updatedState;
      });

      setError(null); // Clear any existing errors on successful update
    } catch (error) {
      handleEmailSignatureError(error, 'email-signature-update');
    }
  };

  // Function to initialize an email signature with default values if it doesn't exist
  const initializeEmailSignature = (cardId) => {
    try {
      if (!cardId) {
        throw new Error('Card ID is required to initialize email signature');
      }

      if (!emailSignatureState[cardId]) {
        setEmailSignatureState(prevState => ({
          ...prevState,
          [cardId]: initialEmailSignatureValues,
        }));
        console.log('Email signature initialized for card:', cardId);
      }
    } catch (error) {
      handleEmailSignatureError(error, 'email-signature-initialization');
    }
  };

  // Function to clear error
  const clearError = () => {
    setError(null);
  };

  // Setter functions for various attributes with error handling
  const setEmailSignatureName = (cardId, name) => {
    try {
      if (typeof name !== 'string') {
        throw new Error('Name must be a string');
      }
      updateEmailSignatureData(cardId, { emailSignatureName: name });
    } catch (error) {
      handleEmailSignatureError(error, 'email-signature-name-update');
    }
  };

  const setEmailSignatureDisclaimer = (cardId, disclaimer) => {
    try {
      if (typeof disclaimer !== 'string') {
        throw new Error('Disclaimer must be a string');
      }
      updateEmailSignatureData(cardId, { emailSignatureDisclaimer: disclaimer });
    } catch (error) {
      handleEmailSignatureError(error, 'email-signature-disclaimer-update');
    }
  };

  const setEmailSignatureQRCodeData = (cardId, qrCodeData) => {
    try {
      updateEmailSignatureData(cardId, { emailSignatureQRCodeData: qrCodeData });
    } catch (error) {
      handleEmailSignatureError(error, 'email-signature-qr-update');
    }
  };

  const setEmailSignatureCompany = (cardId, company) => {
    try {
      if (typeof company !== 'string') {
        throw new Error('Company must be a string');
      }
      updateEmailSignatureData(cardId, { emailSignatureCompany: company });
    } catch (error) {
      handleEmailSignatureError(error, 'email-signature-company-update');
    }
  };

  const setEmailSignatureJobTitle = (cardId, jobTitle) => {
    try {
      if (typeof jobTitle !== 'string') {
        throw new Error('Job title must be a string');
      }
      updateEmailSignatureData(cardId, { emailSignatureJobTitle: jobTitle });
    } catch (error) {
      handleEmailSignatureError(error, 'email-signature-job-title-update');
    }
  };

  const setEmailSignaturePhoneNumber = (cardId, phoneNumber) => {
    try {
      if (typeof phoneNumber !== 'string') {
        throw new Error('Phone number must be a string');
      }
      updateEmailSignatureData(cardId, { emailSignaturePhoneNumber: phoneNumber });
    } catch (error) {
      handleEmailSignatureError(error, 'email-signature-phone-update');
    }
  };

  const setEmailSignatureLocation = (cardId, location) => {
    try {
      if (typeof location !== 'string') {
        throw new Error('Location must be a string');
      }
      updateEmailSignatureData(cardId, { emailSignatureLocation: location });
    } catch (error) {
      handleEmailSignatureError(error, 'email-signature-location-update');
    }
  };

  const setEmailSignatureProfilePictureUrl = (cardId, url) => {
    try {
      // Allow null, undefined, or string values for optional profile picture
      if (url !== null && url !== undefined && typeof url !== 'string') {
        throw new Error('Profile picture URL must be a string, null, or undefined');
      }
      updateEmailSignatureData(cardId, { emailSignatureProfilePictureUrl: url });
    } catch (error) {
      handleEmailSignatureError(error, 'email-signature-profile-picture-update');
    }
  };

  const setEmailSignatureCompanyLogoUrl = (cardId, url) => {
    try {
      // Allow null, undefined, or string values for optional company logo
      if (url !== null && url !== undefined && typeof url !== 'string') {
        throw new Error('Company logo URL must be a string, null, or undefined');
      }
      updateEmailSignatureData(cardId, { emailSignatureCompanyLogoUrl: url });
    } catch (error) {
      handleEmailSignatureError(error, 'email-signature-company-logo-update');
    }
  };

  const setEmailSignatureQRCodeLogoUrl = (cardId, url) => {
    try {
      // Allow null, undefined, or string values for optional QR code logo
      if (url !== null && url !== undefined && typeof url !== 'string') {
        throw new Error('QR code logo URL must be a string, null, or undefined');
      }
      updateEmailSignatureData(cardId, { emailSignatureQRCodeLogoUrl: url });
    } catch (error) {
      handleEmailSignatureError(error, 'email-signature-qr-logo-update');
    }
  };

  const setEmailSignatureBannerImageUrl = (cardId, url) => {
    try {
      // Allow null, undefined, or string values for optional banner image
      if (url !== null && url !== undefined && typeof url !== 'string') {
        throw new Error('Banner image URL must be a string, null, or undefined');
      }
      updateEmailSignatureData(cardId, { emailSignatureBannerImageUrl: url });
    } catch (error) {
      handleEmailSignatureError(error, 'email-signature-banner-update');
    }
  };

  // Setter functions for show/hide toggles with error handling
  const setEmailSignatureIncludeBannerImage = (cardId, include) => {
    try {
      if (typeof include !== 'boolean') {
        throw new Error('Include banner image must be a boolean');
      }
      updateEmailSignatureData(cardId, { emailSignatureIncludeBannerImage: include });
    } catch (error) {
      handleEmailSignatureError(error, 'email-signature-banner-toggle-update');
    }
  };

  const setEmailSignatureIncludeQRCode = (cardId, include) => {
    try {
      if (typeof include !== 'boolean') {
        throw new Error('Include QR code must be a boolean');
      }
      updateEmailSignatureData(cardId, { emailSignatureIncludeQRCode: include });
    } catch (error) {
      handleEmailSignatureError(error, 'email-signature-qr-toggle-update');
    }
  };

  // Setter functions for field visibility flags
  const setEmailSignatureShowName = (cardId, show) => {
    try {
      if (typeof show !== 'boolean') {
        throw new Error('Show name must be a boolean');
      }
      updateEmailSignatureData(cardId, { emailSignatureShowName: show });
    } catch (error) {
      handleEmailSignatureError(error, 'email-signature-show-name-update');
    }
  };

  const setEmailSignatureShowPronouns = (cardId, show) => {
    try {
      if (typeof show !== 'boolean') {
        throw new Error('Show pronouns must be a boolean');
      }
      updateEmailSignatureData(cardId, { emailSignatureShowPronouns: show });
    } catch (error) {
      handleEmailSignatureError(error, 'email-signature-show-pronouns-update');
    }
  };

  const setEmailSignatureShowJobTitle = (cardId, show) => {
    try {
      if (typeof show !== 'boolean') {
        throw new Error('Show job title must be a boolean');
      }
      updateEmailSignatureData(cardId, { emailSignatureShowJobTitle: show });
    } catch (error) {
      handleEmailSignatureError(error, 'email-signature-show-job-title-update');
    }
  };

  const setEmailSignatureShowCompany = (cardId, show) => {
    try {
      if (typeof show !== 'boolean') {
        throw new Error('Show company must be a boolean');
      }
      updateEmailSignatureData(cardId, { emailSignatureShowCompany: show });
    } catch (error) {
      handleEmailSignatureError(error, 'email-signature-show-company-update');
    }
  };

  const setEmailSignatureShowLocation = (cardId, show) => {
    try {
      if (typeof show !== 'boolean') {
        throw new Error('Show location must be a boolean');
      }
      updateEmailSignatureData(cardId, { emailSignatureShowLocation: show });
    } catch (error) {
      handleEmailSignatureError(error, 'email-signature-show-location-update');
    }
  };

  const setEmailSignatureShowPhoneNumber = (cardId, show) => {
    try {
      if (typeof show !== 'boolean') {
        throw new Error('Show phone number must be a boolean');
      }
      updateEmailSignatureData(cardId, { emailSignatureShowPhoneNumber: show });
    } catch (error) {
      handleEmailSignatureError(error, 'email-signature-show-phone-update');
    }
  };

  const setEmailSignatureShowProfilePic = (cardId, show) => {
    try {
      if (typeof show !== 'boolean') {
        throw new Error('Show profile pic must be a boolean');
      }
      updateEmailSignatureData(cardId, { emailSignatureShowProfilePic: show });
    } catch (error) {
      handleEmailSignatureError(error, 'email-signature-show-profile-pic-update');
    }
  };

  const setEmailSignatureShowCompanyLogo = (cardId, show) => {
    try {
      if (typeof show !== 'boolean') {
        throw new Error('Show company logo must be a boolean');
      }
      updateEmailSignatureData(cardId, { emailSignatureShowCompanyLogo: show });
    } catch (error) {
      handleEmailSignatureError(error, 'email-signature-show-company-logo-update');
    }
  };

  // Function to add a link type to the email signature
  const addEmailSignatureLinkType = (cardId, linkType) => {
    try {
      if (!linkType || typeof linkType !== 'string') {
        throw new Error('Link type must be a non-empty string');
      }
      setEmailSignatureState(prevState => {
        const currentLinkTypes = prevState[cardId]?.emailSignatureLinkTypes || [];
        if (!currentLinkTypes.includes(linkType)) {
          return {
            ...prevState,
            [cardId]: {
              ...prevState[cardId],
              emailSignatureLinkTypes: [...currentLinkTypes, linkType],
            },
          };
        }
        return prevState;
      });
    } catch (error) {
      handleEmailSignatureError(error, 'email-signature-link-add');
    }
  };

  // Function to remove a link type from the email signature
  const removeEmailSignatureLinkType = (cardId, linkType) => {
    try {
      if (!linkType || typeof linkType !== 'string') {
        throw new Error('Link type must be a non-empty string');
      }
      setEmailSignatureState(prevState => {
        const currentLinkTypes = prevState[cardId]?.emailSignatureLinkTypes || [];
        return {
          ...prevState,
          [cardId]: {
            ...prevState[cardId],
            emailSignatureLinkTypes: currentLinkTypes.filter(type => type !== linkType),
          },
        };
      });
    } catch (error) {
      handleEmailSignatureError(error, 'email-signature-link-remove');
    }
  };

  return (
    <EmailSignatureContext.Provider
      value={{
        emailSignatureState,
        error,
        setEmailSignatureName,
        setEmailSignatureCompany,
        setEmailSignatureJobTitle,
        setEmailSignaturePhoneNumber,
        setEmailSignatureLocation,
        setEmailSignatureProfilePictureUrl,
        setEmailSignatureCompanyLogoUrl,
        setEmailSignatureQRCodeLogoUrl,
        setEmailSignatureBannerImageUrl,
        setEmailSignatureIncludeBannerImage,
        setEmailSignatureDisclaimer,
        setEmailSignatureIncludeQRCode,
        setEmailSignatureQRCodeData,
        addEmailSignatureLinkType,
        removeEmailSignatureLinkType,
        setEmailSignatureShowName,
        setEmailSignatureShowPronouns,
        setEmailSignatureShowJobTitle,
        setEmailSignatureShowCompany,
        setEmailSignatureShowLocation,
        setEmailSignatureShowPhoneNumber,
        setEmailSignatureShowProfilePic,
        setEmailSignatureShowCompanyLogo,
        initializeEmailSignature, // Expose the initialize function
        clearError, // Expose the clear error function
      }}
    >
      {children}
    </EmailSignatureContext.Provider>
  );
};

// Custom hook to use the EmailSignatureContext
export const useEmailSignatureContext = () => {
  const context = useContext(EmailSignatureContext);
  if (!context) {
    throw new Error('useEmailSignatureContext must be used within an EmailSignatureProvider');
  }
  return context;
};

// Custom hook to initialize email signature based on cardId
export const useInitializeEmailSignature = (cardId) => {
  const { initializeEmailSignature } = useEmailSignatureContext();

  useEffect(() => {
    if (cardId) {
      console.log('Initializing email signature with ID context file:', cardId); // Log to see if initialization is happening
      initializeEmailSignature(cardId);
    }
  }, [cardId, initializeEmailSignature]);
};
